import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { collection, addDoc, Timestamp } from 'firebase/firestore'
import { db } from '../firebaseConfig'
import Swal from 'sweetalert2'
import Navbar from '../components/navbar/navbar'
import PageTitle from '../components/page-title'
import FooterTop from '../components/footer-top'
import Footer from '../components/footer'
import SEO from '../components/SEO'
import { generateContactPageSchema } from '../utils/schema'

interface ContactForm {
  name: string;
  email: string;
  subject: string;
  message: string;
}

export default function Contact() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<ContactForm>({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState<Partial<ContactForm>>({});
  const [loading, setLoading] = useState(false);
  // Sprint 7 (F1-007): anti-spam defensas client-side.
  // Honeypot: campo invisible que bots suelen rellenar.
  const [honey, setHoney] = useState('');
  // Timestamp de montaje: bots suelen enviar <3s después de cargar.
  const [formMountedAt] = useState(Date.now());

  const validateForm = (): boolean => {
    const newErrors: Partial<ContactForm> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('contact.required');
    }
    if (!formData.email.trim()) {
      newErrors.email = t('contact.required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('contact.invalidEmail');
    }
    if (!formData.subject.trim()) {
      newErrors.subject = t('contact.required');
    }
    if (!formData.message.trim()) {
      newErrors.message = t('contact.required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof ContactForm]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    // Sprint 7 (F1-007): honeypot + time-gate + rate-limit localStorage.
    if (honey.trim()) {
      // Bot detectado: fake success para no revelar la trampa.
      Swal.fire({ icon: 'success', title: t('contact.formSuccess'), confirmButtonColor: '#0c2339' });
      setFormData({ name: '', email: '', subject: '', message: '' });
      return;
    }
    if (Date.now() - formMountedAt < 3000) {
      Swal.fire({ icon: 'warning', title: 'Tómate un momento', text: 'Completa el formulario con calma antes de enviar.', confirmButtonColor: '#0c2339' });
      return;
    }
    const lastSentStr = localStorage.getItem('ampi_contact_last_sent');
    const lastSent = lastSentStr ? parseInt(lastSentStr, 10) : 0;
    if (lastSent && Date.now() - lastSent < 60_000) {
      const wait = Math.ceil((60_000 - (Date.now() - lastSent)) / 1000);
      Swal.fire({ icon: 'info', title: 'Demasiado pronto', text: `Espera ${wait}s antes de enviar otro mensaje.`, confirmButtonColor: '#0c2339' });
      return;
    }

    setLoading(true);
    try {
      await addDoc(collection(db, 'contacto_mensajes'), {
        ...formData,
        fechaEnvio: Timestamp.now(),
        leido: false,
        respondido: false
      });

      Swal.fire({
        icon: 'success',
        title: t('contact.formSuccess'),
        confirmButtonColor: '#0c2339'
      });

      setFormData({ name: '', email: '', subject: '', message: '' });
      localStorage.setItem('ampi_contact_last_sent', String(Date.now()));
    } catch (error) {
      console.error('Error enviando mensaje:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: t('contact.formError'),
        confirmButtonColor: '#0c2339'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
        <SEO
          title="Contacto AMPI Riviera Nayarit"
          description="Contacta a AMPI Riviera Nayarit. Escríbenos para información sobre afiliación, servicios inmobiliarios y bienes raíces en Bahía de Banderas y Riviera Nayarit."
          path="/contact"
          keywords="contacto AMPI riviera nayarit, contactar inmobiliaria nayarit, afiliación AMPI, bienes raíces contacto bahía de banderas"
          schema={generateContactPageSchema()}
        />
        <Navbar transparent={false}/>

        <PageTitle
          title={t('contact.pageTitle')}
          subtitle={t('contact.pageSubtitle')}
        />

        <section>
            <div className="container">
                <div className="row">
                    <div className="col-lg-7 col-md-7">
                        <form onSubmit={handleSubmit}>
                            {/* Sprint 7 (F1-007): honeypot — oculto para humanos, irresistible para bots */}
                            <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', opacity: 0, height: 0, width: 0, overflow: 'hidden' }}>
                                <label htmlFor="website_url">Website (no llenar)</label>
                                <input
                                  type="text"
                                  id="website_url"
                                  name="website_url"
                                  tabIndex={-1}
                                  autoComplete="off"
                                  value={honey}
                                  onChange={(e) => setHoney(e.target.value)}
                                />
                            </div>
                            <div className="row">
                                <div className="col-lg-6 col-md-6">
                                    <div className="form-group">
                                        <label className='mb-2'>{t('contact.name')}</label>
                                        <input
                                          type="text"
                                          name="name"
                                          className={`form-control simple ${errors.name ? 'is-invalid' : ''}`}
                                          value={formData.name}
                                          onChange={handleChange}
                                        />
                                        {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                    </div>
                                </div>
                                <div className="col-lg-6 col-md-6">
                                    <div className="form-group">
                                        <label className='mb-2'>{t('contact.email')}</label>
                                        <input
                                          type="email"
                                          name="email"
                                          className={`form-control simple ${errors.email ? 'is-invalid' : ''}`}
                                          value={formData.email}
                                          onChange={handleChange}
                                        />
                                        {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                                    </div>
                                </div>
                            </div>

                            <div className="form-group">
                                <label className='mb-2'>{t('contact.subject')}</label>
                                <input
                                  type="text"
                                  name="subject"
                                  className={`form-control simple ${errors.subject ? 'is-invalid' : ''}`}
                                  value={formData.subject}
                                  onChange={handleChange}
                                />
                                {errors.subject && <div className="invalid-feedback">{errors.subject}</div>}
                            </div>

                            <div className="form-group">
                                <label className='mb-2'>{t('contact.message')}</label>
                                <textarea
                                  name="message"
                                  className={`form-control simple ${errors.message ? 'is-invalid' : ''}`}
                                  value={formData.message}
                                  onChange={handleChange}
                                  rows={5}
                                ></textarea>
                                {errors.message && <div className="invalid-feedback">{errors.message}</div>}
                            </div>

                            <div className="form-group">
                                <button
                                  className="btn btn-primary px-5 rounded"
                                  type="submit"
                                  disabled={loading}
                                >
                                  {loading ? t('common.loading') : t('contact.submitButton')}
                                </button>
                            </div>
                        </form>
                    </div>

                    <div className="col-lg-5 col-md-5">
                        <div className="contact-info">
                            <h2>{t('contact.getInTouch')}</h2>
                            <p>{t('contact.getInTouchText')}</p>

                            <div className="cn-info-detail mt-4">
                                <div className="cn-info-icon">
                                    <i className="fa-solid fa-house"></i>
                                </div>
                                <div className="cn-info-content">
                                    <h4 className="cn-info-title">{t('contact.reachUs')}</h4>
                                    Av. México 1220, Col. Centro<br/>
                                    Nuevo Vallarta, Nayarit<br/>
                                    C.P. 63735, México
                                </div>
                            </div>
                            <div className="cn-info-detail">
                                <div className="cn-info-icon">
                                    <i className="fa-solid fa-envelope-circle-check"></i>
                                </div>
                                <div className="cn-info-content">
                                    <h4 className="cn-info-title">{t('contact.dropMail')}</h4>
                                    contacto@ampirivieranayarit.com<br/>
                                    info@ampirivieranayarit.com
                                </div>
                            </div>
                            <div className="cn-info-detail">
                                <div className="cn-info-icon">
                                    <i className="fa-solid fa-phone-volume"></i>
                                </div>
                                <div className="cn-info-content">
                                    <h4 className="cn-info-title">{t('contact.callUs')}</h4>
                                    +52 (322) 226 8686<br/>
                                    +52 (322) 297 7835
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <FooterTop bg="theme-bg"/>
        <Footer/>
    </>
  )
}
